import { SeededRandom } from '@utils/SeededRandom';

interface SoundParameters {
  seed: number;
  duration: number;
  sampleRate?: number;
}

interface WeaponSoundParams extends SoundParameters {
  type: 'laser' | 'railgun' | 'torpedo';
  pitch?: number;
  modulation?: number;
}

interface ImpactSoundParams extends SoundParameters {
  type: 'shield' | 'hull';
  intensity?: number;
}

export interface BaseSoundParams extends SoundParameters {
  type: 'warp' | 'weld' | 'blip' | 'launch' | 'deny' | 'chime';
  /** Base frequency; a negative blip pitch sweeps down instead of up */
  pitch?: number;
}

export class ProceduralSoundGenerator {
  private audioContext: AudioContext | null = null;
  private soundCache: Map<string, AudioBuffer> = new Map();

  constructor() {
    // Don't create AudioContext in constructor - wait for user interaction
  }

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return this.audioContext;
  }

  private getCacheKey(params: any): string {
    return JSON.stringify(params);
  }

  generateWeaponSound(params: WeaponSoundParams): AudioBuffer {
    const cacheKey = this.getCacheKey(params);
    const cached = this.soundCache.get(cacheKey);
    if (cached) return cached;

    let buffer: AudioBuffer;
    switch (params.type) {
      case 'laser':
        buffer = this.generateLaserSound(params);
        break;
      case 'railgun':
        buffer = this.generateRailgunSound(params);
        break;
      case 'torpedo':
        buffer = this.generateTorpedoSound(params);
        break;
    }

    this.soundCache.set(cacheKey, buffer);
    return buffer;
  }

  generateImpactSound(params: ImpactSoundParams): AudioBuffer {
    const cacheKey = this.getCacheKey(params);
    const cached = this.soundCache.get(cacheKey);
    if (cached) return cached;

    let buffer: AudioBuffer;
    switch (params.type) {
      case 'shield':
        buffer = this.generateShieldImpactSound(params);
        break;
      case 'hull':
        buffer = this.generateHullImpactSound(params);
        break;
    }

    this.soundCache.set(cacheKey, buffer);
    return buffer;
  }

  /** Mothership sounds: shipyard, repairs, deployment, launch and credits. */
  generateBaseSound(params: BaseSoundParams): AudioBuffer {
    const cacheKey = this.getCacheKey(params);
    const cached = this.soundCache.get(cacheKey);
    if (cached) return cached;

    const rng = new SeededRandom(params.seed);
    const sampleRate = params.sampleRate || this.getAudioContext().sampleRate;
    const length = Math.floor(sampleRate * params.duration);
    const buffer = this.getAudioContext().createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);
    const sample = BASE_SOUND_SAMPLERS[params.type];

    let phase = 0;
    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const progress = t / params.duration;
      const { frequency, value } = sample(t, progress, params.pitch, rng, phase);
      phase += (2 * Math.PI * frequency) / sampleRate;
      data[i] = value;
    }

    this.soundCache.set(cacheKey, buffer);
    return buffer;
  }

  private generateLaserSound(params: WeaponSoundParams): AudioBuffer {
    const rng = new SeededRandom(params.seed);
    const sampleRate = params.sampleRate || this.getAudioContext().sampleRate;
    const duration = params.duration;
    const length = sampleRate * duration;
    const buffer = this.getAudioContext().createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    const basePitch = params.pitch || rng.range(800, 1200);
    const endPitch = basePitch * rng.range(0.3, 0.5);
    const modFreq = params.modulation || rng.range(20, 40);

    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const progress = t / duration;
      
      // Frequency sweep
      const freq = basePitch * Math.pow(endPitch / basePitch, progress);
      
      // Amplitude envelope (quick attack, exponential decay)
      const envelope = Math.pow(1 - progress, 2);
      
      // Add some modulation for texture
      const modulation = 1 + 0.3 * Math.sin(2 * Math.PI * modFreq * t);
      
      // Mix of sine and square waves
      const sine = Math.sin(2 * Math.PI * freq * t);
      const square = Math.sign(sine);
      const mix = sine * 0.7 + square * 0.3;
      
      // Add some noise
      const noise = (rng.next() - 0.5) * 0.1;
      
      data[i] = (mix * modulation + noise) * envelope * 0.5;
    }

    return buffer;
  }

  private generateRailgunSound(params: WeaponSoundParams): AudioBuffer {
    const rng = new SeededRandom(params.seed);
    const sampleRate = params.sampleRate || this.getAudioContext().sampleRate;
    const duration = params.duration;
    const length = sampleRate * duration;
    const buffer = this.getAudioContext().createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    // Railgun: charge up + powerful discharge
    const chargeTime = duration * 0.6;
    const dischargeTime = duration * 0.4;

    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      if (t < chargeTime) {
        // Charge phase: rising frequency and amplitude
        const chargeProgress = t / chargeTime;
        const freq = 50 + 450 * chargeProgress;
        const amplitude = chargeProgress * 0.3;
        
        // Oscillating charge sound
        sample = Math.sin(2 * Math.PI * freq * t) * amplitude;
        
        // Add electrical crackling
        if (rng.boolean(0.1)) {
          sample += (rng.next() - 0.5) * amplitude * 2;
        }
      } else {
        // Discharge phase: explosive release
        const dischargeProgress = (t - chargeTime) / dischargeTime;
        const envelope = Math.exp(-dischargeProgress * 5);
        
        // Low frequency punch
        const bassBoom = Math.sin(2 * Math.PI * 60 * t) * 0.5;
        
        // High frequency sizzle
        const sizzle = Math.sin(2 * Math.PI * 2000 * t) * 0.3;
        
        // White noise burst
        const noise = (rng.next() - 0.5) * 0.4;
        
        sample = (bassBoom + sizzle + noise) * envelope;
      }

      data[i] = sample * 0.7;
    }

    return buffer;
  }

  private generateTorpedoSound(params: WeaponSoundParams): AudioBuffer {
    const rng = new SeededRandom(params.seed);
    const sampleRate = params.sampleRate || this.getAudioContext().sampleRate;
    const duration = params.duration;
    const length = sampleRate * duration;
    const buffer = this.getAudioContext().createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    // Torpedo: whoosh + engine hum
    const launchPhase = duration * 0.3;

    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      let sample = 0;

      if (t < launchPhase) {
        // Launch whoosh
        const launchProgress = t / launchPhase;
        const envelope = Math.sin(Math.PI * launchProgress);
        
        // Filtered noise for whoosh
        const noise = rng.next() - 0.5;
        const freq = 500 + 1500 * (1 - launchProgress);
        sample = noise * Math.sin(2 * Math.PI * freq * t) * envelope;
      }

      // Continuous engine hum
      const engineFreq = 120 + rng.range(-10, 10);
      const engineHum = Math.sin(2 * Math.PI * engineFreq * t) * 0.2;
      
      // Add harmonics
      const harmonic1 = Math.sin(2 * Math.PI * engineFreq * 2 * t) * 0.1;
      const harmonic2 = Math.sin(2 * Math.PI * engineFreq * 3 * t) * 0.05;
      
      sample += engineHum + harmonic1 + harmonic2;

      data[i] = sample * 0.6;
    }

    return buffer;
  }

  private generateShieldImpactSound(params: ImpactSoundParams): AudioBuffer {
    const rng = new SeededRandom(params.seed);
    const sampleRate = params.sampleRate || this.getAudioContext().sampleRate;
    const duration = params.duration;
    const length = sampleRate * duration;
    const buffer = this.getAudioContext().createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    const intensity = params.intensity || 1;

    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const progress = t / duration;
      
      // Quick attack, medium decay
      const envelope = progress < 0.1 ? progress * 10 : Math.exp(-(progress - 0.1) * 4);
      
      // High frequency shimmer
      const shimmerFreq = rng.range(3000, 5000);
      const shimmer = Math.sin(2 * Math.PI * shimmerFreq * t);
      
      // Mid frequency resonance
      const resonanceFreq = rng.range(800, 1200);
      const resonance = Math.sin(2 * Math.PI * resonanceFreq * t);
      
      // Modulation for energy field effect
      const modFreq = rng.range(30, 50);
      const modulation = 1 + 0.5 * Math.sin(2 * Math.PI * modFreq * t);
      
      // Mix components
      const sample = (shimmer * 0.6 + resonance * 0.4) * modulation * envelope;
      
      data[i] = sample * intensity * 0.5;
    }

    return buffer;
  }

  private generateHullImpactSound(params: ImpactSoundParams): AudioBuffer {
    const rng = new SeededRandom(params.seed);
    const sampleRate = params.sampleRate || this.getAudioContext().sampleRate;
    const duration = params.duration;
    const length = sampleRate * duration;
    const buffer = this.getAudioContext().createBuffer(1, length, sampleRate);
    const data = buffer.getChannelData(0);

    const intensity = params.intensity || 1;

    for (let i = 0; i < length; i++) {
      const t = i / sampleRate;
      const progress = t / duration;
      
      // Sharp attack, quick decay
      const envelope = progress < 0.05 ? progress * 20 : Math.exp(-(progress - 0.05) * 8);
      
      // Metallic clang
      const baseFreq = rng.range(200, 400);
      const metallic = Math.sin(2 * Math.PI * baseFreq * t);
      
      // Inharmonic overtones for metallic sound
      const overtone1 = Math.sin(2 * Math.PI * baseFreq * 2.3 * t) * 0.5;
      const overtone2 = Math.sin(2 * Math.PI * baseFreq * 3.7 * t) * 0.3;
      const overtone3 = Math.sin(2 * Math.PI * baseFreq * 5.2 * t) * 0.2;
      
      // Impact thud (low frequency)
      const thudFreq = rng.range(40, 80);
      const thud = Math.sin(2 * Math.PI * thudFreq * t) * 0.7;
      
      // Noise burst for impact
      const noise = (rng.next() - 0.5) * 0.3 * Math.exp(-progress * 10);
      
      // Mix all components
      const sample = (thud + metallic + overtone1 + overtone2 + overtone3 + noise) * envelope;
      
      data[i] = sample * intensity * 0.6;
    }

    return buffer;
  }

  playSound(buffer: AudioBuffer, volume: number = 1.0): void {
    const source = this.getAudioContext().createBufferSource();
    const gainNode = this.getAudioContext().createGain();
    
    source.buffer = buffer;
    gainNode.gain.value = volume;
    
    source.connect(gainNode);
    gainNode.connect(this.getAudioContext().destination);
    
    source.start();
  }

  // Resume audio context if suspended (required for some browsers)
  async ensureAudioContext(): Promise<void> {
    if (this.getAudioContext().state === 'suspended') {
      await this.getAudioContext().resume();
    }
  }
}

type BaseSampler = (
  t: number, progress: number, pitch: number | undefined, rng: SeededRandom, phase: number
) => { frequency: number; value: number };

/** Per-sample synthesis for each mothership sound. `phase` is the running phase of the swept tone. */
const BASE_SOUND_SAMPLERS: Record<BaseSoundParams['type'], BaseSampler> = {
  // Rising shimmer that swells and snaps into place, like a hull materialising
  warp: (t, progress, _pitch, rng, phase) => {
    const frequency = 180 + 1400 * progress * progress;
    const swell = progress < 0.85 ? progress / 0.85 : Math.exp(-(progress - 0.85) * 30);
    const shimmer = Math.sin(phase) * 0.5 + Math.sin(phase * 1.5 + Math.sin(t * 40)) * 0.25;
    const air = (rng.next() - 0.5) * 0.25 * swell;
    const snap = progress > 0.85 ? Math.sin(2 * Math.PI * 1760 * t) * Math.exp(-(progress - 0.85) * 25) * 0.4 : 0;
    return { frequency, value: (shimmer * swell + air + snap) * 0.45 };
  },
  // Crackling arc with a mains hum, in irregular bursts
  weld: (t, progress, _pitch, rng) => {
    const burst = Math.sin(t * 23) * Math.sin(t * 9.7) > -0.2 ? 1 : 0.25;
    const crackle = rng.next() < 0.18 ? (rng.next() - 0.5) * 1.6 : (rng.next() - 0.5) * 0.35;
    const hum = Math.sin(2 * Math.PI * 120 * t) * 0.18 + Math.sin(2 * Math.PI * 2400 * t) * 0.06;
    const envelope = Math.min(1, progress * 20) * (1 - progress * 0.6);
    return { frequency: 0, value: (crackle * burst + hum) * envelope * 0.5 };
  },
  // Two-tone confirmation; positive pitch steps up (deploy), negative steps down (recall)
  blip: (t, progress, pitch = 660) => {
    const base = Math.abs(pitch);
    const up = pitch >= 0;
    const frequency = progress < 0.45 ? (up ? base : base * 1.5) : (up ? base * 1.5 : base);
    const envelope = Math.exp(-((progress % 0.5) * 9)) * Math.min(1, progress * 60);
    const value = (Math.sin(2 * Math.PI * frequency * t) + Math.sin(2 * Math.PI * frequency * 2 * t) * 0.2) * envelope;
    return { frequency, value: value * 0.35 };
  },
  // Engine roar that builds and sweeps away as the fleet leaves
  launch: (t, progress, _pitch, rng, phase) => {
    const frequency = 50 + 260 * progress;
    const build = Math.min(1, progress * 2.5) * Math.exp(-Math.max(0, progress - 0.55) * 5);
    const roar = (rng.next() - 0.5) * 0.9 * build;
    const tone = Math.sin(phase) * 0.45 * build + Math.sin(phase * 2.01) * 0.2 * build;
    const thump = Math.sin(2 * Math.PI * 45 * t) * Math.exp(-progress * 12) * 0.6;
    return { frequency, value: (roar + tone + thump) * 0.5 };
  },
  // Low double buzz for a refused action
  deny: (t, progress) => {
    const gate = progress < 0.4 || (progress > 0.55 && progress < 0.95) ? 1 : 0;
    const square = Math.sign(Math.sin(2 * Math.PI * 140 * t)) * 0.5 + Math.sin(2 * Math.PI * 70 * t) * 0.5;
    return { frequency: 140, value: square * gate * 0.22 };
  },
  // Bright coin tick
  chime: (t, progress, pitch = 1760) => {
    const envelope = Math.exp(-progress * 7) * Math.min(1, progress * 80);
    const value = Math.sin(2 * Math.PI * pitch * t) + Math.sin(2 * Math.PI * pitch * 2.76 * t) * 0.3;
    return { frequency: pitch, value: value * envelope * 0.25 };
  },
};
